import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Card, 
  CardContent 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Sun, AlertCircle, Activity, Dumbbell, Flame, Heart, Save, Droplet } from 'lucide-react';
import { NutritionJournal } from '@/types';
import { toast } from 'sonner';

interface ActivityTabProps {
  journal: NutritionJournal;
  setJournal: (journal: NutritionJournal) => void;
  containerVariants: any;
  itemVariants: any;
}

const ActivityTab: React.FC<ActivityTabProps> = ({
  journal,
  setJournal,
  containerVariants,
  itemVariants
}) => {
  const [journalId, setJournalId] = useState<string>(journal.id);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [steps, setSteps] = useState(journal.steps || 0);
  const [workoutType, setWorkoutType] = useState<'cardio' | 'strength' | 'flexibility' | 'other'>(
    journal.workout?.type as any || 'cardio'
  );
  const [workoutMinutes, setWorkoutMinutes] = useState(journal.workout?.minutes || 0);
  const [workoutDescription, setWorkoutDescription] = useState(journal.workout?.description || '');
  const [restLevel, setRestLevel] = useState(journal.restLevel || 5);
  const [energyLevel, setEnergyLevel] = useState(journal.energyLevel || 5);
  const [sunExposure, setSunExposure] = useState(journal.sunExposure || 0);
  const [showStepsSuccess, setShowStepsSuccess] = useState(false);

  useEffect(() => {
    if (journal.id !== journalId) {
      console.log(`[ActivityTab] Journal ID changed from ${journalId} to ${journal.id}`);
      console.log(`[ActivityTab] New journal steps: ${journal.steps}, sunExposure: ${journal.sunExposure}`);
      
      setSteps(journal.steps || 0);
      setWorkoutType(journal.workout?.type as any || 'cardio');
      setWorkoutMinutes(journal.workout?.minutes || 0);
      setWorkoutDescription(journal.workout?.description || '');
      setRestLevel(journal.restLevel || 5);
      setEnergyLevel(journal.energyLevel || 5);
      setSunExposure(journal.sunExposure || 0);
      setShowStepsSuccess(journal.steps >= 10000);
      
      setJournalId(journal.id);
      setHasUnsavedChanges(false);
    }
  }, [journal.id, journal.steps, journal.workout, journal.restLevel, journal.energyLevel, journal.sunExposure, journalId]);

  const stepsProgress = Math.min(Math.round((steps / 10000) * 100), 100);

  const updateSteps = useCallback((value: number) => {
    if (value === steps) return;
    
    console.log(`[ActivityTab] Updating steps to ${value} for journal ID ${journal.id}`);
    
    setSteps(value);
    setShowStepsSuccess(value >= 10000);
    setHasUnsavedChanges(true);
  }, [journal.id, steps]);

  const handleWorkoutTypeChange = useCallback((value: 'cardio' | 'strength' | 'flexibility' | 'other') => {
    if (value === workoutType) return;
    
    setWorkoutType(value);
    setHasUnsavedChanges(true);
  }, [workoutType]);

  const handleWorkoutMinutesChange = useCallback((value: number) => {
    if (value === workoutMinutes) return;
    
    setWorkoutMinutes(value);
    setHasUnsavedChanges(true);
  }, [workoutMinutes]);

  const handleWorkoutDescriptionChange = useCallback((value: string) => {
    if (value === workoutDescription) return;
    
    setWorkoutDescription(value);
    setHasUnsavedChanges(true);
  }, [workoutDescription]);

  const handleRestLevelChange = useCallback((value: number) => {
    if (value === restLevel) return;
    
    setRestLevel(value);
    setHasUnsavedChanges(true);
  }, [restLevel]);

  const handleEnergyLevelChange = useCallback((value: number) => {
    if (value === energyLevel) return;
    
    setEnergyLevel(value);
    setHasUnsavedChanges(true);
  }, [energyLevel]);

  const handleSunExposureChange = useCallback((value: number) => {
    if (value === sunExposure) return;
    
    console.log(`[ActivityTab] Updating sun exposure to ${value} for journal ID ${journal.id}`);
    
    setSunExposure(value);
    setHasUnsavedChanges(true);
  }, [journal.id, sunExposure]);

  const handleSaveActivity = () => {
    try {
      // Create a deep copy of the journal to avoid reference issues
      const updatedJournal = JSON.parse(JSON.stringify(journal));
      
      // Update all fields with current state values
      updatedJournal.steps = steps;
      updatedJournal.sunExposure = sunExposure;
      updatedJournal.restLevel = restLevel;
      updatedJournal.energyLevel = energyLevel;
      
      // Ensure workout object is properly structured
      updatedJournal.workout = {
        type: workoutType,
        minutes: workoutMinutes,
        intensity: 'medium',
        description: workoutDescription
      };
      
      console.log('[ActivityTab] Saving activity data:', {
        steps,
        sunExposure,
        restLevel, 
        energyLevel,
        workout: updatedJournal.workout
      });
      
      // Send the updated journal to the parent component
      setJournal(updatedJournal);
      setHasUnsavedChanges(false);
      
      // Scroll to top after saving
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show success toast
      toast.success("Cambios guardados", {
        description: "Tu actividad ha sido actualizada correctamente",
        duration: 3000,
      });
      
      // Dispatch event for others components to update
    } catch (error) {
      console.error('Error saving activity:', error);
      
      // Show error toast
      toast.error("Error al guardar", {
        description: "No se pudieron guardar los cambios. Inténtalo de nuevo.",
        duration: 3000,
      });
    }
  };

  const getWorkoutIcon = (type: string) => {
    switch (type) {
      case 'cardio':
        return <Activity className="h-5 w-5" />;
      case 'strength':
        return <Dumbbell className="h-5 w-5" />;
      case 'flexibility':
        return <Heart className="h-5 w-5" />;
      default:
        return <Flame className="h-5 w-5" />;
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      
      <motion.div variants={itemVariants}>
        <Card className="mb-4 overflow-hidden border-0 bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-green-700 flex items-center">
                Pasos diarios
                {showStepsSuccess && (
                  <span className="ml-2 h-5 w-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">
                    ✓
                  </span>
                )}
              </h3>
              <span className="text-lg font-bold text-green-700">{stepsProgress}%</span>
            </div>
            
            <div className="h-3 bg-green-200 rounded-full mt-2 overflow-hidden">
              <div 
                className={`h-full ${showStepsSuccess ? 'bg-green-500' : 'bg-green-400'} transition-all duration-500`}
                style={{ width: `${stepsProgress}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between mt-2 text-sm text-green-700">
              <span>0</span>
              <span>5000</span>
              <span>10000</span>
            </div>
            
            <div className="mt-4 flex items-center gap-4">
              <Input
                type="number"
                min={0}
                max={30000}
                value={steps}
                onChange={(e) => updateSteps(Number(e.target.value))}
                className="w-24"
              />
              <Button 
                onClick={() => updateSteps(steps + 1000)}
                variant="outline"
                size="sm"
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                +1000
              </Button>
              
              <Button 
                onClick={() => updateSteps(10000)}
                variant="outline"
                size="sm"
                className="border-green-300 text-green-700 hover:bg-green-50 ml-auto"
              >
                Objetivo: 10000
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <Card className="mb-4 border-0 overflow-hidden bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-amber-700 flex items-center">
                <Sun className="h-5 w-5 mr-2" />
                Exposición al sol
              </h3>
              
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-200 text-amber-800">
                <AlertCircle className="h-3 w-3 mr-1" />
                Mínimo recomendado: 20 min/día
              </span>
            </div>
            
            <p className="text-xs text-amber-600 mb-3">
              La exposición moderada al sol ayuda a la producción de vitamina D, esencial para la absorción de calcio y fósforo.
            </p>
            
            <div className="flex gap-4 items-center">
              <Input
                type="number"
                min={0}
                max={180}
                value={sunExposure}
                onChange={(e) => handleSunExposureChange(Number(e.target.value))}
                className="w-24"
                placeholder="Minutos"
              />
              <span className="text-amber-700 text-sm">minutos</span>
              
              <Button 
                onClick={() => handleSunExposureChange(20)}
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-50 ml-auto"
              >
                Mínimo recomendado
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <Card className="mb-4 border-0 overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <h3 className="font-semibold text-blue-700 mb-2">Ejercicio</h3>
            <p className="text-xs text-blue-600 mb-3">
              Se recomienda realizar al menos 30 minutos de actividad física al día para mantener un estilo de vida saludable.
            </p>
            
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-4">
                <div className="w-full sm:w-auto">
                  <label className="text-sm mb-1 block text-blue-600">Tipo</label>
                  <Select 
                    value={workoutType} 
                    onValueChange={(value: any) => handleWorkoutTypeChange(value)}
                  >
                    <SelectTrigger className="w-full sm:w-40 bg-white border-blue-200">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cardio">Cardio</SelectItem>
                      <SelectItem value="strength">Fuerza</SelectItem>
                      <SelectItem value="flexibility">Flexibilidad</SelectItem>
                      <SelectItem value="other">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-full sm:w-auto">
                  <label className="text-sm mb-1 block text-blue-600">Duración (min)</label>
                  <Input
                    type="number"
                    min={0}
                    value={workoutMinutes}
                    onChange={(e) => handleWorkoutMinutesChange(Number(e.target.value))}
                    className="w-full sm:w-32 border-blue-200"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm mb-1 block text-blue-600">Descripción</label>
                <Textarea
                  value={workoutDescription}
                  onChange={(e) => handleWorkoutDescriptionChange(e.target.value)}
                  placeholder="Describe tu actividad..."
                  className="resize-none border-blue-200"
                  rows={2}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <Card className="mb-4 border-0 overflow-hidden bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <h3 className="font-semibold text-purple-700 mb-3">¿Cómo te sientes hoy?</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-sm text-purple-600">Nivel de descanso</label>
                  <span className="text-sm font-medium text-purple-700">{restLevel}/10</span>
                </div>
                <Slider
                  value={[restLevel]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleRestLevelChange(value[0])}
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-purple-500">
                  <span>Agotado</span>
                  <span>Descansado</span>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <label className="text-sm text-purple-600">Nivel de energía</label>
                  <span className="text-sm font-medium text-purple-700">{energyLevel}/10</span>
                </div>
                <Slider
                  value={[energyLevel]}
                  min={1}
                  max={10}
                  step={1}
                  onValueChange={(value) => handleEnergyLevelChange(value[0])}
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-purple-500">
                  <span>Baja</span>
                  <span>Alta</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      <div className="flex justify-center mt-6 mb-4">
        <Button 
          onClick={handleSaveActivity} 
          size="lg"
          className="bg-primary hover:bg-primary/90 text-white"
          type="button"
        >
          <Save className="h-5 w-5 mr-2" />
          Guardar actividad
        </Button>
      </div>
    </motion.div>
  );
};

export default ActivityTab;
